/*
 * Copyright (c) 2022 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Tuesday, April 12th 2022, 10:18:19 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import React from "react";
import PostAddIcon from "@mui/icons-material/PostAdd";
import Typography from "@mui/material/Typography";
import CorporateFareIcon from "@mui/icons-material/CorporateFare";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import TaskIcon from "@mui/icons-material/Task";
import { FileText as DocumentIcon, File as SubmissionIcon, User as ApplicantIcon } from "react-feather";
import { COLORSCHEME } from "theme/styles";
import { OSDialog, OSDialogProps, OSTabs, IconText } from "ui-widgets";
import { useAPIFetchContextTags } from "common/services";
import { APIItemDisplayWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { StandardTagsInfoComponent } from "./StandardTagsInfoComponent";
import { FormTagsInfoComponent } from "./FormTagsInfoComponent";

export type ContextTagSelectorProps = Pick<OSDialogProps, "open" | "onClose"> &
  Pick<React.ComponentProps<typeof StandardTagsInfoComponent>, "onCopyContextTag" | "copiedMessage"> & {
    contextTagsAPIFilters?: Record<string, string | number>;
  };

export const ContextTagSelector = ({
  contextTagsAPIFilters,
  open,
  onClose,
  onCopyContextTag,
  copiedMessage,
}: ContextTagSelectorProps) => {
  const { contextTags, httpResponseStatusCode, isAPIError, isMakingAPIRequest } = useContextTagSelectorLogic({
    contextTagsAPIFilters,
    open,
  });

  const tabs = { labels: [], panels: [] };

  if (contextTags?.applicant_tags) {
    tabs.labels.push(<>{`Logged In User Tags`}</>);
    tabs.panels.push(
      <StandardTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        tagsInfo={contextTags.logged_in_user_tags}
        tagButtonIcon={<AccountCircleIcon />}
        key="logged_in_user_tags"
      />
    );
  }

  if (contextTags?.applicant_tags) {
    tabs.labels.push(<>{`Applicant Tags`}</>);
    tabs.panels.push(
      <StandardTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        tagsInfo={contextTags.applicant_tags}
        tagButtonIcon={<ApplicantIcon color={COLORSCHEME.secondary} />}
        key="applicant_tags"
      />
    );
  }

  contextTags?.form_tags?.forEach((form_tag) => {
    tabs.labels.push(<>{`${form_tag.form_title} Tags`}</>);

    const form_tag_components: React.ReactNode[] = [];

    if (form_tag.form_type === "application_form" && contextTags?.application_tags) {
      form_tag_components.push(
        <StandardTagsInfoComponent
          onCopyContextTag={onCopyContextTag}
          copiedMessage={copiedMessage}
          tagsInfo={contextTags.application_tags}
          tagButtonIcon={<SubmissionIcon color={COLORSCHEME.secondary} />}
          key="application_tags"
        />
      );
    }

    form_tag_components.push(
      <FormTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        key={form_tag.form_id}
        field_tags={form_tag.field_tags}
      />
    );

    tabs.panels.push(<>{form_tag_components.map((component) => component)}</>);
  });

  if (contextTags?.workflow_tags) {
    tabs.labels.push(<>{`Workflow Tags`}</>);
    tabs.panels.push(
      <StandardTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        tagsInfo={contextTags.workflow_tags}
        tagButtonIcon={<TaskIcon />}
      />
    );
  }

  if (contextTags?.organization_tags) {
    tabs.labels.push(<>{`Organization Tags`}</>);
    tabs.panels.push(
      <StandardTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        tagsInfo={contextTags.organization_tags}
        tagButtonIcon={<CorporateFareIcon />}
      />
    );
  }

  if (contextTags?.document_tags) {
    tabs.labels.push(<>{`Document Tags`}</>);
    tabs.panels.push(
      <StandardTagsInfoComponent
        onCopyContextTag={onCopyContextTag}
        copiedMessage={copiedMessage}
        tagsInfo={contextTags.document_tags}
        tagButtonIcon={<DocumentIcon color={COLORSCHEME.secondary} />}
      />
    );
  }

  return (
    <OSDialog
      open={open}
      onClose={onClose}
      popUpTitle={
        <IconText
          icon={<PostAddIcon color="primary" />}
          textComponent={<Typography variant="h5">Context Tags</Typography>}
        />
      }
    >
      <APIItemDisplayWrapper
        showLoader={isMakingAPIRequest}
        loaderType="linear"
        isAPIError={isAPIError}
        httpResponseStatusCode={httpResponseStatusCode}
      >
        <OSTabs tabsStyle={{ minWidth: 200 }} tabLabels={tabs.labels} tabPanels={tabs.panels} />
      </APIItemDisplayWrapper>
    </OSDialog>
  );
};

const useContextTagSelectorLogic = ({
  contextTagsAPIFilters = {},
  open,
}: Pick<ContextTagSelectorProps, "open" | "contextTagsAPIFilters">) => {
  const {
    apiItem: contextTags,
    fetchItem,
    httpResponseStatusCode,
    isAPIError,
    isMakingAPIRequest,
  } = useAPIFetchContextTags();

  React.useEffect(() => {
    if (open && !contextTags) fetchItem(null, { apiQueryParams: contextTagsAPIFilters });
    //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, contextTags]);

  return { contextTags, httpResponseStatusCode, isAPIError, isMakingAPIRequest };
};
